import { useMemo } from "react";
import SearchBox from "./SearchBox";
import ColumnItem from "./ColumnItem";
import { useSelector } from "react-redux";
import { CardContent } from "../../../components/ui/card";
import { ScrollArea } from "../../../components/ui/scroll-area";
import CollapsibleCard from "../../../components/CollapsibleCard";
import { PINNED_FIELDS, useExtCustomColumns } from "../../../contexts/extractions/ExtCustomColumnContext";

const AvailableColumnsPanel = ({
  selectedIds,
  onToggle,
  searchTerm,
  onSearchChange
}) => {
  const { usedFieldsInSelectedCustomColumns } = useExtCustomColumns();

  const { columnListValues, extractionViewOnly } = useSelector((state) => state.Extractions);

  // Split columns into pinned (always at top, locked) and the rest.
  // Pinned columns are shown even when they don't match the search term so the
  // user always has a visual anchor at the top of the list.
  const { pinnedColumns, filteredColumns } = useMemo(() => {
    const pinned = [];
    const rest = [];

    (columnListValues || []).forEach(col => {
      if (PINNED_FIELDS.has(col?.Field)) {
        pinned.push(col);
      } else if (col?.Field?.toLowerCase().includes(searchTerm.toLowerCase())) {
        rest.push(col);
      }
    });

    return { pinnedColumns: pinned, filteredColumns: rest };
  }, [columnListValues, searchTerm]);

  return (
    <CollapsibleCard
        title="All Columns"
        className="flex-1"
    >
        <CardContent className="p-0 h-full grid grid-rows-[auto_1fr] gap-4">
            {/* Search Box - auto sized */}
            <div>
                <SearchBox value={searchTerm} onChange={onSearchChange} />
            </div>

            {/* Scrollable Area - takes remaining space (1fr) */}
            <ScrollArea className="h-[180px]">
                {pinnedColumns.length === 0 && filteredColumns.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                        No columns found
                    </div>
                ) : (
                    <>
                        {/* Pinned fields — always at top, checkbox disabled */}
                        {pinnedColumns.map(column => (
                            <ColumnItem
                                key={column?.Field}
                                column={column}
                                isSelected={true}
                                onToggle={() => {}} // no-op: pinned fields cannot be toggled
                                disabled={true}     // visually locked regardless of view-only mode
                                isPinned={true}
                            />
                        ))}

                        {/* Regular selectable columns */}
                        {filteredColumns.map((column) => {
                          const isSelected = selectedIds?.includes(column.Field) ?? false;
                          const isLockedUsedField = isSelected && usedFieldsInSelectedCustomColumns?.has(column.Field);

                          return (
                            <ColumnItem
                              key={column?.Field}
                              column={column}
                              isSelected={isSelected}
                              onToggle={onToggle}
                              disabled={extractionViewOnly || isLockedUsedField}
                            />
                          );
                        })}
                    </>
                )}
            </ScrollArea>
        </CardContent>
    </CollapsibleCard>
  );
};

export default AvailableColumnsPanel;